import React, { useState, useEffect,useCallback } from 'react';
import {
    Table,Spin, Button, Modal, Form, Input, DatePicker,Checkbox, Select, Popconfirm, message, Row, Col
} from 'antd';
import moment from 'moment';
import api from '../api/axios'; // Adjust the path as needed

const { Option } = Select;

export default function AdHocPayments() {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [visible, setVisible] = useState(false);
    const [editingPayment, setEditingPayment] = useState(null);
    const [form] = Form.useForm();

    // Filters: arrays for multi-select filters
    const [filterPaidTo, setFilterPaidTo] = useState([]);
    const [filterMode, setFilterMode] = useState([]);
    const [filterAdvisedBy, setFilterAdvisedBy] = useState([]);
    const [filterAdviseDate, setFilterAdviseDate] = useState(null);
    const [dateCondition, setDateCondition] = useState('greater');
    const [filterAmount, setFilterAmount] = useState('');
    const [amountCondition, setAmountCondition] = useState('greater');
    const [filterPaymentStatus, setFilterPaymentStatus] = useState([]);
    const [filterInvoiceNoEmpty, setFilterInvoiceNoEmpty] = useState(null);

    const [loading, setLoading] = useState(false);
    const [sorter, setSorter] = useState({});

    const [rowEdits, setRowEdits] = useState({});

    const [duplicateInfo, setDuplicateInfo] = useState({ duplicate: false, paymentId: null });
    const [utrValue, setUtrValue] = useState('');

    // for search from adhoList
    const [adhocMatch, setAdhocMatch] = useState(null); // { Name, Message, Amount, Contact }
    const [searchingAdhoc, setSearchingAdhoc] = useState(false);

    // confirm delete with code
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [deleteCode, setDeleteCode] = useState('');
    const DELETE_CODE = process.env.REACT_APP_DELETE_CODE || 'DEEPAK1234';



    useEffect(() => {
        fetchData();
    }, [sorter]);

    const fetchData = () => {
        setLoading(true);

        const sortField = sorter.field || '';
        const sortOrder = sorter.order === 'ascend' ? 'ASC' : sorter.order === 'descend' ? 'DESC' : '';

        let url = '/adhocpayments';
        if (sortField && sortOrder) {
            url += `?sortField=${sortField}&sortOrder=${sortOrder}`;
        }

        fetch(url)
            .then(res => res.json())
            .then(data => {
                setData(data);
                applyFilters(data); // Pass the freshest data to filter function
                setLoading(false);
            })
            .catch(() => setLoading(false));
    };

    useEffect(() => {
        applyFilters();
    }, [
        filterPaidTo,
        filterMode,
        filterAdvisedBy,
        filterAdviseDate,
        dateCondition,
        filterAmount,
        amountCondition,
        filterPaymentStatus,
        filterInvoiceNoEmpty,
        data,
    ]);

    const fetchPayments = async () => {
        try {
            const res = await api.get('/adhocpayments');
            setData(res.data);
            setFilteredData(res.data);
        } catch (err) {
            message.error('Failed to fetch AdHocPayments');
        }
    };

    const showModal = (record) => {
        setEditingPayment(record || null);
        if (record) {
            form.setFieldsValue({
                ...record,
                AdviseDate: record.AdviseDate ? moment(record.AdviseDate) : null,
                PaymentDate: record.PaymentDate ? moment(record.PaymentDate) : null,
                PaymentStatus: record.PaymentStatus === 'Y',
            });
        } else {
            form.resetFields();
        }
        setVisible(true);
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            if (values.AdviseDate) values.AdviseDate = values.AdviseDate.format('YYYY-MM-DD');
            if (values.PaymentDate) values.PaymentDate = values.PaymentDate.format('YYYY-MM-DD');

            // Transform checkbox boolean to 'Y'/'N'
            values.PaymentStatus = values.PaymentStatus ? 'Y' : 'N';

            if (editingPayment) await api.put(`/adhocpayments/${editingPayment.PaymentID}`, values);
            else await api.post('/adhocpayments', values);

            message.success(editingPayment ? 'Payment updated!' : 'Payment added!');
            setVisible(false);
            fetchPayments();
        } catch {
            message.error('Failed to save payment');
        }
    };

    // Handler when UTR field changes
    const onUtrChange = async e => {
        const value = e.target.value;
        setUtrValue(value);

        if (!value) {
            setDuplicateInfo({ duplicate: false, paymentId: null });
            return;
        }

        try {
            const res = await api.get(`/adhocpayments/check-duplicate-utr?utr=${encodeURIComponent(value)}`);
            setDuplicateInfo(res.data);
        } catch {
            setDuplicateInfo({ duplicate: false, paymentId: null });
        }
    };


    const handleDelete = async (paymentID) => {
        try {
            await api.delete(`/adhocpayments/${paymentID}`);
            message.success('Payment deleted!');
            setShowDeleteModal(false);
            setDeleteCode('');
            fetchPayments();
        } catch {
            message.error('Failed to delete payment');
        }
    };

    const convertToCSV = (objArray) => {
        const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
        let str = '';
        const header = Object.keys(array[0]).join(',') + '\r\n';
        str += header;

        for (let i = 0; i < array.length; i++) {
            let line = '';
            for (const index in array[i]) {
                if (line !== '') line += ',';

                let field = array[i][index];
                if (typeof field === 'string' && field.includes(',')) {
                    field = `"${field.replace(/"/g, '""')}"`; // Escape quotes for CSV format
                }
                line += field;
            }
            str += line + '\r\n';
        }
        return str;
    };

    const exportToCSV = () => {
        if (!filteredData.length) {
            message.info('No data to export');
            return;
        }

        const csv = convertToCSV(filteredData);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', 'AdHocPayments.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const applyFilters = (dataToFilter = data) => {
        let filtered = [...dataToFilter];
        if (filterPaidTo.length > 0) {
            filtered = filtered.filter((item) =>
                filterPaidTo.some((val) => item.PaidTo?.toLowerCase().includes(val.toLowerCase()))
            );
        }
        if (filterMode.length > 0) {
            filtered = filtered.filter((item) => filterMode.includes(item.Mode));
        }
        if (filterAdvisedBy.length > 0) {
            filtered = filtered.filter((item) =>
                filterAdvisedBy.some((val) => item.AdvisedBy?.toLowerCase().includes(val.toLowerCase()))
            );
        }
        if (filterPaymentStatus.length > 0) {
            filtered = filtered.filter(item => filterPaymentStatus.includes(item.PaymentStatus));
        }
        if (filterAdviseDate) {
            filtered = filtered.filter((item) => {
                if (!item.AdviseDate) return false;
                const itemDate = moment(item.AdviseDate);
                return dateCondition === 'greater'
                    ? itemDate.isSameOrAfter(filterAdviseDate, 'day')
                    : itemDate.isSameOrBefore(filterAdviseDate, 'day');
            });
        }
        if (filterAmount) {
            const amountNum = Number(filterAmount);
            if (!isNaN(amountNum)) {
                filtered = filtered.filter((item) => {
                    const amt = Number(item.Amount);
                    return amountCondition === 'greater' ? amt > amountNum : amt < amountNum;
                });
            }
        }
        if (filterInvoiceNoEmpty !== null) {
            filtered = filtered.filter(item => {
                const isEmpty = !item.InvoiceNo || item.InvoiceNo.trim() === '';
                return filterInvoiceNoEmpty ? isEmpty : !isEmpty;
            });
        }
        setFilteredData(filtered);
    };

    const resetFilters = () => {
        setFilterPaidTo([]);
        setFilterMode([]);
        setFilterAdvisedBy([]);
        setFilterAdviseDate(null);
        setDateCondition('greater');
        setFilterAmount('');
        setAmountCondition('greater');
        setFilteredData(data);
        setFilterPaymentStatus([]);
        setFilterInvoiceNoEmpty(null);
    };

    // Dynamically compute unique options for PaidTo, Mode, AdvisedBy fields
    const uniquePaidTo = [...new Set(data.map((item) => item.PaidTo).filter(Boolean))];
    const uniqueMode = [...new Set(data.map((item) => item.Mode).filter(Boolean))];
    const uniqueAdvisedBy = [...new Set(data.map((item) => item.AdvisedBy).filter(Boolean))];

    const columns = [
        {title: 'Payment Id',dataIndex: 'PaymentID',sorter: true,sortDirections: ['ascend', 'descend'],onHeaderCell: () => ({onClick: () => {}})},
        {title: 'Paid To',dataIndex: 'PaidTo',sorter: true,sortDirections: ['ascend', 'descend'],onHeaderCell: () => ({onClick: () => {}})},
        {title: 'Amount',dataIndex: 'Amount',sorter: true,sortDirections: ['ascend', 'descend'],onHeaderCell: () => ({onClick: () => {}})},
        {title: 'TDS_Deducted',dataIndex: 'TDS_Deducted',sorter: true,sortDirections: ['ascend', 'descend'],onHeaderCell: () => ({onClick: () => {}})},
        {title: 'Payment Date',dataIndex: 'PaymentDate',sorter: true,sortDirections: ['ascend', 'descend'],onHeaderCell: () => ({onClick: () => {}})},
        {title: 'Mode',dataIndex: 'Mode',sorter: true,sortDirections: ['ascend', 'descend'],onHeaderCell: () => ({onClick: () => {}})},
        {title: 'UTR',dataIndex: 'UTR',sorter: true,sortDirections: ['ascend', 'descend'],onHeaderCell: () => ({onClick: () => {}})},
        {title: 'PAN',dataIndex: 'PAN',sorter: true,sortDirections: ['ascend', 'descend'],onHeaderCell: () => ({onClick: () => {}})},
        {title: 'Advised By',dataIndex: 'AdvisedBy',sorter: true,sortDirections: ['ascend', 'descend'],onHeaderCell: () => ({onClick: () => {}})},
        {title: 'Advise Date',dataIndex: 'AdviseDate',sorter: true,sortDirections: ['ascend', 'descend'],onHeaderCell: () => ({onClick: () => {}})},
        {title: 'Advise Mode',dataIndex: 'AdviseMode',sorter: true,sortDirections: ['ascend', 'descend'],onHeaderCell: () => ({onClick: () => {}})},
        {
            title: 'Payment Status',
            dataIndex: 'PaymentStatus',
            render: (val, record) => (
                <Checkbox
                    checked={rowEdits[record.PaymentID]?.PaymentStatus ?? val === 'Y'}
                    onChange={e => {
                        setRowEdits(edits => ({
                            ...edits,
                            [record.PaymentID]: {
                                ...edits[record.PaymentID],
                                PaymentStatus: e.target.checked,
                            },
                        }));
                    }}
                />
            ),
        },
        {
            title: 'Invoice No',
            dataIndex: 'InvoiceNo',
            render: (val, record) => (
                <Input
                    value={rowEdits[record.PaymentID]?.InvoiceNo ?? val}
                    onChange={e =>
                        setRowEdits(edits => ({
                            ...edits,
                            [record.PaymentID]: {
                                ...edits[record.PaymentID],
                                InvoiceNo: e.target.value,
                            },
                        }))
                    }
                />
            ),
        },
        {
            title: 'Remark',
            dataIndex: 'Remark',
            render: (val, record) => (
                <Input
                    value={rowEdits[record.PaymentID]?.Remark ?? val}
                    onChange={e =>
                        setRowEdits(edits => ({
                            ...edits,
                            [record.PaymentID]: {
                                ...edits[record.PaymentID],
                                Remark: e.target.value,
                            },
                        }))
                    }
                />
            ),
        },
        {
            title: 'Action',
            render: (_, record) => (
                <>
                    <Button
                        type="primary"
                        onClick={async () => {
                            const newValues = rowEdits[record.PaymentID] || {};
                            // Transform PaymentStatus to 'Y'/'N'
                            const PaymentStatus = newValues.PaymentStatus ? 'Y' : 'N';
                            // Send API update
                            await api.put(`/adhocpayments/${record.PaymentID}`, {
                                ...record,
                                ...newValues,
                                PaymentStatus,
                            });
                            message.success('Payment updated');
                            setRowEdits(edits => ({ ...edits, [record.PaymentID]: {} }));
                            fetchPayments();
                        }}
                    >
                        Update
                    </Button>
                    <Button type="link" onClick={() => showModal(record)}>
                        Edit
                    </Button>
                    <Button
                        type="link"
                        danger
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowDeleteModal(true);
                        }}
                    >
                        Delete
                    </Button>

                </>
            ),
        },
    ];

    const handleTableChange = (pagination, filters, sorter) => {
        if (!sorter.order) {
            // Ignore no sort to prevent unwanted reset
            return;
        }
        setSorter(sorter);
    };

    // for adhocList search
    // Debounced adhoc search
    const searchAdhoc = useCallback(async (searchTerm) => {
        if (searchTerm.length < 2) {
            setAdhocMatch(null);
            setSearchingAdhoc(false);
            return;
        }

        setSearchingAdhoc(true);
        try {
            const response = await api.get('/adhoclist/search', {
                params: { name: searchTerm }
            });

            if (response.data && response.data.Name) {
                setAdhocMatch(response.data);
            } else {
                setAdhocMatch(null);
            }
        } catch (error) {
            console.error('Adhoc search failed:', error);
            setAdhocMatch(null);
        } finally {
            setSearchingAdhoc(false);
        }
    }, []);

// Debounce effect
    useEffect(() => {
        let timeoutId;
        const searchTerm = form.getFieldValue('PaidTo') || '';

        if (searchTerm.length >= 3) {
            timeoutId = setTimeout(() => {
                searchAdhoc(searchTerm);
            }, 400);
        } else {
            setAdhocMatch(null);
        }

        return () => clearTimeout(timeoutId);
    }, [form.getFieldValue('PaidTo')]);

    return (
        <>
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col>
                    <Select
                        mode="multiple"
                        placeholder="Filter Paid To"
                        value={filterPaidTo}
                        onChange={setFilterPaidTo}
                        allowClear
                        style={{ minWidth: 160 }}
                    >
                        {uniquePaidTo.map((val) => (
                            <Option key={val} value={val}>
                                {val}
                            </Option>
                        ))}
                    </Select>
                </Col>
                <Col>
                    <Select
                        mode="multiple"
                        placeholder="Filter Mode"
                        value={filterMode}
                        onChange={setFilterMode}
                        allowClear
                        style={{ minWidth: 120 }}
                    >
                        {uniqueMode.map((val) => (
                            <Option key={val} value={val}>
                                {val}
                            </Option>
                        ))}
                    </Select>
                </Col>
                <Col>
                    <Select
                        mode="multiple"
                        placeholder="Filter Advised By"
                        value={filterAdvisedBy}
                        onChange={setFilterAdvisedBy}
                        allowClear
                        style={{ minWidth: 160 }}
                    >
                        {uniqueAdvisedBy.map((val) => (
                            <Option key={val} value={val}>
                                {val}
                            </Option>
                        ))}
                    </Select>
                </Col>
                <Col style={{ display: 'flex', alignItems: 'center' }}>
                    <Select value={dateCondition} onChange={setDateCondition} style={{ width: 80 }}>
                        <Option value="greater">&gt;</Option>
                        <Option value="less">&lt;</Option>
                    </Select>
                    <DatePicker
                        placeholder="Filter Advise Date"
                        value={filterAdviseDate}
                        onChange={setFilterAdviseDate}
                        allowClear
                        style={{ marginLeft: 4, width: 160 }}
                    />
                </Col>
                <Col style={{ display: 'flex', alignItems: 'center' }}>
                    <Select value={amountCondition} onChange={setAmountCondition} style={{ width: 80 }}>
                        <Option value="greater">&gt;</Option>
                        <Option value="less">&lt;</Option>
                    </Select>
                    <Input
                        placeholder="Amount"
                        value={filterAmount}
                        onChange={(e) => setFilterAmount(e.target.value)}
                        type="number"
                        style={{ width: 100, marginLeft: 4 }}
                        allowClear
                    />
                </Col>
                <Col>
                    <Select
                        mode="multiple"
                        placeholder="Filter Payment Status"
                        value={filterPaymentStatus}
                        onChange={setFilterPaymentStatus}
                        allowClear
                        style={{ minWidth: 160 }}
                    >
                        <Option value="Y">Paid (Y)</Option>
                        <Option value="N">Not Paid (N)</Option>
                    </Select>
                </Col>
                <Col>
                    <Select
                        placeholder="Filter InvoiceNo"
                        value={filterInvoiceNoEmpty}
                        onChange={value => setFilterInvoiceNoEmpty(value)}
                        allowClear
                        style={{ minWidth: 140 }}
                    >
                        <Option value={null}>All</Option>
                        <Option value={true}>Empty</Option>
                        <Option value={false}>Not Empty</Option>
                    </Select>
                </Col>
                <Col>
                    <Button type="primary" onClick={resetFilters}>
                        Reset
                    </Button>
                </Col>
                <Col>
                    <Button style={{ marginLeft: 8 }} onClick={exportToCSV}>
                        Export CSV
                    </Button>

                </Col>
            </Row>

            <Button type="primary" onClick={() => showModal(null)} style={{ marginBottom: 16 }}>
                Add Payment
            </Button>

            <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="PaymentID"
                loading={loading}
                onChange={handleTableChange}
            />


            {/*} add modal for delete confirm */}
            <Modal
                title="Confirm Delete Payment"
                open={showDeleteModal}
                onCancel={() => {
                    setShowDeleteModal(false);
                    setDeleteCode('');
                }}
                footer={null}
                width={400}
            >
                {currentRecord && (
                    <>
                        <p><strong>Delete this payment?</strong></p>
                        <p><strong>Payment ID:</strong> {currentRecord.PaymentID}</p>
                        <div style={{ margin: '16px 0' }}>
                            <Input.Password
                                placeholder="Enter delete code"
                                value={deleteCode}
                                onChange={(e) => setDeleteCode(e.target.value)}
                                onPressEnter={() => handleDelete(currentRecord.PaymentID)}
                                size="large"
                            />
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <Button onClick={() => setShowDeleteModal(false)} style={{ marginRight: 8 }}>
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                danger
                                disabled={deleteCode !== DELETE_CODE}
                                onClick={() => handleDelete(currentRecord.PaymentID)}
                            >
                                Delete Payment
                            </Button>
                        </div>
                    </>
                )}
            </Modal>


            <Modal visible={visible} width={900} onOk={handleOk} onCancel={() => setVisible(false)} title={editingPayment ? 'Edit Payment' : 'Add Payment'} destroyOnClose>
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="PaidTo"
                        label={
                            <span>
                                    Paid To
                                    {searchingAdhoc && <span style={{ color: '#1890ff' }}> 🔍 searching...</span>}
                            </span>
                        }
                        rules={[{ required: true, message: 'Paid To is required' }]}
                    >
                        <Input
                            placeholder="Enter name or search AdHoc List..."
                            onBlur={() => {
                                // Search on blur (tab out/move to next field)
                                const value = form.getFieldValue('PaidTo') || '';
                                if (value && value.length >= 2) {
                                    searchAdhoc(value);
                                }
                            }}
                        />
                    </Form.Item>

                    {adhocMatch && (
                        <div style={{
                            margin: '8px 0',
                            padding: '8px 12px',
                            background: '#fff1f0',
                            border: '1px solid #ff7875',
                            borderRadius: 6,
                            fontSize: 13
                        }}>
                        <span style={{ color: '#000000', fontWeight: 500 }}>  {/* red text */}
                                 ✓ Matched: {adhocMatch.Name}
                        </span>
                            <br />
                                Message: {adhocMatch.Message || '—'} |
                                Amount: ₹{Number(adhocMatch.Amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })} |
                                Contact: {adhocMatch.Contact || '—'}
                        </div>
                    )}

                    {!adhocMatch && form.getFieldValue('PaidTo') && form.getFieldValue('PaidTo').length >= 2 && (
                        <div style={{
                            margin: '8px 0',
                            padding: '6px 12px',
                            background: '#f6ffed',       // light green bg
                            border: '1px solid #b7eb8f', // green border
                            borderRadius: 6,
                            fontSize: 13,
                            color: '#52c41a'             // Ant Design green
                        }}>
                            Name not found in AdHoc List
                        </div>

                    )}

                    <Form.Item name="Amount" label="Amount" rules={[{ required: true }]}>
                        <Input type="number" />
                    </Form.Item>
                    <Form.Item name="TDS_Deducted" label="TDS_Deducted" rules={[{ required: true }]}>
                        <Input type="real" />
                    </Form.Item>
                    <Form.Item name="PaymentDate" label="Payment Date">
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="Mode" label="Mode">
                        <Select>
                            <Option value="UPI">UPI</Option>
                            <Option value="Cash">Cash</Option>
                            <Option value="RTGS">RTGS</Option>
                            <Option value="Cheque">Cheque</Option>
                            <Option value="IMPS">IMPS</Option>
                            <Option value="NEFT">NEFT</Option>
                            <Option value="Bank to Bank">Bank to Bank</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        label={
                            <>
                                UTR{' '}
                                {utrValue && duplicateInfo.duplicate ? (
                                    <span style={{ color: 'red' }}>
          Duplicate for PaymentId: {duplicateInfo.paymentId || '(unknown)'}<br />
          Paid To: {duplicateInfo.paidTo || '(unknown)'}
        </span>
                                ) : utrValue ? (
                                    <span style={{ color: 'green' }}>Duplicate = No</span>
                                ) : null}
                            </>
                        }
                        name="UTR"
                        //rules={[{ required: true, message: 'Please enter UTR' }]}
                    >
                        <Input value={utrValue} onChange={onUtrChange} />
                    </Form.Item>
                    <Form.Item name="PAN" label="PAN">
                        <Input />
                    </Form.Item>
                    <Form.Item name="AdvisedBy" label="Advised By">
                        <Input />
                    </Form.Item>
                    <Form.Item name="AdviseDate" label="Advise Date">
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="AdviseMode" label="Advise Mode">
                        <Input />
                    </Form.Item>
                    <Form.Item name="PaymentStatus" valuePropName="checked">
                        <Checkbox>Payment Status (Paid)</Checkbox>
                    </Form.Item>
                    <Form.Item name="InvoiceNo" label="Invoice No" >
                        <Input />
                    </Form.Item>
                    <Form.Item name="Remark" label="Remark">
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}
